import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { askNowActions } from "./../../../Store/AskNowTicket";
import SnackBar from "components/SnackBar/SnackBar";
import moment from "moment";
import SpinnerWithTime from "components/Spinner/SpinnerWithTime";
import PropTypes from "prop-types";
import { Box } from "@mui/material";

//Defining trainees grid columns contains Oracle Id, First Name, Last Name, Date Assigned, Candidate Id
const columns = [
  { field: "oracleId", headerName: "OracleId", width: 80, hideable: false },
  {
    field: "firstName",
    headerName: "First Name",
    width: 120,
    // description: "Column contains First Name",
    // editable: true,
  },
  { field: "lastName", headerName: "Last Name", width: 120 },
  {
    field: "dateAssigned",
    headerName: "Date Assigned",
    type: "date",
    width: 150,
  },
  {
    field: "candidateID",
    headerName: "Candidate Id",
    type: "number",
    width: 90,
  },
  {
    field: "activeInAD",
    headerName: "Active in AD",
    type: "string",
    width: 80,
  },
  { field: "RITM", headerName: "RITM#", width: 140 },
  {
    field: "cppClientCode",
    headerName: "CPP Client Code",
    type: "number",
    width: 120,
  },
  {
    field: "cppProgramCode",
    headerName: "CPP Program Code",
    type: "number",
    width: 120,
  },
  {
    field: "cppProjectCode",
    headerName: "CPP Project Code",
    type: "number",
    width: 120,
  },

  {
    field: "locationId",
    headerName: "Location ID",
    type: "number",
    width: 120,
  },
  {
    field: "jobTitle",
    headerName: "Job Code",
    type: "number",
    minWidth: 330,
  },
  {
    field: "department",
    headerName: "Department",
    type: "number",
    width: 330,
  },
  {
    field: "classTraineeID",
    headerName: "Class Trainee Id",
  },
];

//props contains the roster id from roster configuration page.
const ActiveTrainee = (props) => {
  const askNowDetail = useSelector((state) => state.askNow);
  const traineeDetails = useSelector((state) => state.generalStore);
  const assignTraineeData = useSelector((state) => state.assignTrainees);
  const [requestType, setRequestType] = useState(
    "Activation (New Hire or Reactivation)",
  );
  const [loading, setLoading] = useState(false);
  const [idmLoading, setIdmLoading] = useState(false);
  const [trainees, setTrainees] = useState([]);
  const [currentProjectCodes, setCurrentProjectCodes] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const dispatch = useDispatch();
  //update asknowticket store values.
  dispatch(askNowActions.activeTrainee(selectedRows));

  const getTciTtfDate = (rosterConfigDetails) => {
    const { rosterId } = props;
    const details = rosterConfigDetails?.find(
      (item) => item?.rosterId === rosterId,
    );
    if (details?.techEquipmentSolutionId === 14) {
      return details?.ttfDateTime ?? null;
    }
    return details?.tciDateTime ?? details?.techTestDateTime ?? null;
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  useEffect(() => {
    setIdmLoading(traineeDetails.loadingState);
  }, [traineeDetails.loadingState]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (askNowDetail.traineeDetailsResponse.message === "Success") {
        try {
          await fetchData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to fetch data:", error);
          }
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [askNowDetail.traineeDetailsResponse]);

  useEffect(() => {
    setRequestType(askNowDetail.typeOfRequest);
  }, [askNowDetail.typeOfRequest]);

  useEffect(() => {
    let cancelled = false;

    const handleReset = async () => {
      if (props.resetActiveTrainee === true) {
        dispatch(askNowActions.activeTrainee(""));
        setSelectedRows([]);

        try {
          await fetchData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to fetch data:", error);
          }
        }
      }
    };

    handleReset();

    return () => {
      cancelled = true;
    };
  }, [props.resetActiveTrainee, askNowDetail.traineeDetailsResponse]);

  const fetchData = async () => {
    setLoading(true);

    //Fetching trainee details from store.
    const traineeDetails = askNowDetail.traineeDetailsResponse;

    if (traineeDetails.message === "Success") {
      setTrainees(traineeDetails.data["traineeDetails"]);
      dispatch(
        askNowActions.waveNumber(traineeDetails.data["rosterDetail"]["wave"]),
      );
      dispatch(
        askNowActions.reqForDateTime(
          traineeDetails.data["rosterDetail"]["reqDateTime"],
        ),
      );
      const tcittfDate = getTciTtfDate(assignTraineeData?.rosterConfigDetails);
      dispatch(askNowActions.tCITTDStartDate(tcittfDate));
      dispatch(
        askNowActions.productionTrainingStart(
          traineeDetails.data["rosterDetail"]["productTrainingDate"],
        ),
      );
      dispatch(
        askNowActions.productionDate(
          traineeDetails.data["rosterDetail"]["productionDate"],
        ),
      );
      dispatch(
        askNowActions.currentClientProgram(
          traineeDetails.data["rosterDetail"]["cppProjectCode"],
        ),
      );
    } else {
      setSnack({
        open: true,
        message: "Failed to load trainees",
        severity: "error",
      });
    }

    setLoading(false);
  };

  //Check the trainee not empty and map trainee data with mentioned columns
  useEffect(() => {
    setCurrentProjectCodes(
      trainees?.map((trainee, index) => {
        return {
          id: index,
          oracleId: trainee.oracleId,
          firstName: trainee.preferredFirstName,
          lastName: trainee.preferredLastName,
          dateAssigned: moment(
            trainee.dateAdded ? trainee.dateAdded : null,
          ).format("YYYY-MM-DD hh:mm:ss A"),
          candidateID: trainee.candidateId,
          classTraineeID: trainee.classTraineeId,
          RITM: trainee.idmTicketNumber,
          cppClientCode: trainee.cppClientCode,
          cppProgramCode: trainee.cppProgramCode,
          cppProjectCode: trainee.cppProjectCode,
          activeInAD: trainee.activeInAD === true ? "Yes" : "No",
          jobTitle: trainee.jobTitle,
          locationId: trainee.locationId,
          department: trainee.department,
        };
      }),
    );
    const cppRowData = trainees?.map((row) => row.cppProjectCode);
    const modifiedCppRowData = cppRowData?.filter((code) => code !== "");
    dispatch(askNowActions.currentProjectCodes(cppRowData));
  }, [trainees]);

  return (
    <Box sx={{ height: 645, width: "100%", padding: 20 }}>
      {loading || idmLoading ? (
        <SpinnerWithTime
          initialTime={
            traineeDetails.activeTrainees === 0
              ? 30
              : 30 + Math.floor(1.5 * traineeDetails.activeTrainees)
          }
        />
      ) : null}
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>

      <DataGrid
        columnVisibilityModel={{
          // Hide columns classTraineeID, the other columns will remain visible
          classTraineeID: false,
        }}
        rows={currentProjectCodes}
        columns={columns}
        pageSize={100}
        rowsPerPageOptions={[100]}
        checkboxSelection
        disableSelectionOnClick
        rowHeight={25}
        isRowSelectable={(rowData) =>
          requestType !== "Activation (New Hire or Reactivation)"
            ? rowData.row.RITM === "" && rowData.row.activeInAD === "Yes"
            : rowData.row.RITM === "" && rowData.row.activeInAD !== "Yes"
        }
        //Access the row ids( oracle id) inside onSelectionModelChange callback.

        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const pickedRows = currentProjectCodes.filter((row) =>
            selectedIDs.has(row.oracleId),
          );
          setSelectedRows(
            pickedRows.map((a) => {
              return {
                oracleId: a.oracleId,
                classTraineeId: a.classTraineeID,
                firstName: a.firstName,
                lastName: a.lastName,
                cppClientCode: a.cppClientCode,
                cppProgramCode: a.cppProgramCode,
                cppProjectCode: a.cppProjectCode,
                jobTitle: a.jobTitle,
                locationId: a.locationId,
                department: a.department,
              };
            }),
          );
        }}
      />
    </Box>
  );
};

ActiveTrainee.propTypes = {
  rosterId: PropTypes.any,
  resetActiveTrainee: PropTypes.any,
};

export default ActiveTrainee;
